// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { type AskV2ThreadMessage } from "./types";
import { type AskV2TransportApi } from "./useAskV2Transport";

import { AskPunttThread } from "../../components/ask-puntt-thread";

type FeedMember = {
  _id: string;
  name?: string | null;
  email?: string | null;
  avatar?: string | null;
};

type ThreadProps = {
  transport: AskV2TransportApi;
  presentation?: "chat" | "feed";
  feedActorName?: string;
  feedActorAvatar?: string;
  feedMembers?: FeedMember[];
  currentUserId?: string;
};

function transportMessageKey(message: AskV2ThreadMessage): string {
  return message.serverId ?? message.clientId;
}

export function AskV2Thread({
  transport,
  presentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
}: ThreadProps) {
  const { t } = useTranslation("ui");

  const normalizedFeedMembers = useMemo(
    () =>
      feedMembers?.map((member) => ({
        _id: member._id,
        name: member.name ?? undefined,
        email: member.email ?? undefined,
        avatar: member.avatar,
      })),
    [feedMembers],
  );

  const { setComposer, send } = transport;
  // A form the agent drew is answered the way everything here is answered:
  // as the next message, in words. The composer is the one send path, so
  // the formatted answer goes through it rather than a second API route.
  const submitAnswers = useCallback(
    (formattedAnswers: string) => {
      setComposer(formattedAnswers);
      void send();
    },
    [setComposer, send],
  );

  const transportById = useMemo(() => {
    const map = new Map<string, AskV2ThreadMessage>();
    for (const message of transport.messages) {
      map.set(transportMessageKey(message), message);
      map.set(message.clientId, message);
    }
    return map;
  }, [transport.messages]);

  if (
    transport.displayMessages.length === 0 &&
    transport.messages.length === 0
  ) {
    return null;
  }

  const failedById = new Map(
    transport.messages
      .filter((message) => message.status === "failed")
      .map((message) => [transportMessageKey(message), message]),
  );

  return (
    <div data-testid="ask-v2-thread" data-presentation={presentation}>
      <AskPunttThread
        messages={transport.displayMessages}
        presentation={presentation}
        chatId={transport.chatId ?? undefined}
        waitingForAI={transport.inFlight}
        feedActorName={feedActorName}
        feedActorAvatar={feedActorAvatar}
        feedMembers={normalizedFeedMembers}
        currentUserId={currentUserId}
        onSubmitAnswers={submitAnswers}
        hideAgentChromeForMessage={(message) => {
          const transportMessage = transportById.get(message._id);
          return (
            transportMessage?.status === "aborted" &&
            message.role === "assistant"
          );
        }}
        renderBeforeMessage={(message) => {
          if (message.role !== "user" || !message.quotedContexts?.length) {
            return null;
          }
          return (
            <Flex gap="2" wrap="wrap" justify="end">
              {message.quotedContexts.map((ctx) => (
                <Flex
                  key={ctx.id}
                  align="center"
                  className="max-w-full rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                  data-testid={`ask-v2-sent-quoted-context-${message._id}-${ctx.id}`}
                >
                  <Text
                    size="1"
                    className="max-w-[200px] truncate text-puntt-neutral-gray-11"
                  >
                    {ctx.text}
                  </Text>
                </Flex>
              ))}
            </Flex>
          );
        }}
        renderAfterMessage={(message) => {
          const failed = failedById.get(message._id);
          if (failed) {
            return (
              <div className="mt-2 flex items-center gap-2">
                <Text as="p" size="1" color="red">
                  {t("askV2.transport.failed")}
                </Text>
                <Button
                  size="1"
                  variant="ghost"
                  data-testid={`ask-v2-retry-${failed.clientId}`}
                  onClick={() => {
                    void transport.retry(failed.clientId);
                  }}
                >
                  {t("askV2.transport.retry")}
                </Button>
              </div>
            );
          }

          const transportMessage = transportById.get(message._id);
          if (transportMessage?.status === "sending") {
            return (
              <Text
                as="p"
                size="1"
                color="gray"
                data-testid={`ask-v2-message-sending-${transportMessageKey(transportMessage)}`}
              >
                {t("askV2.transport.sending")}
              </Text>
            );
          }
          if (transportMessage?.status === "aborted") {
            return (
              <Text as="p" size="1" color="gray">
                {t("askV2.transport.stopped")}
              </Text>
            );
          }
          return null;
        }}
      />
    </div>
  );
}
